<template>
  <div class="pending__main">
    <full-spinner v-if="isPendingPaymentsLoading"/>
    <bb-container v-else>
      <bb-row justify="between">
        <h4 class="f-color-navy f-medium"> {{ translations.title.account }}</h4>
        <bb-button
          v-if="isMobile"
          data-testid="add-pending-payment"
          color="black"
          inverted
          size="sm"
          :label="translations.addPendingButtonText"
          @click="onCreate"
        />
      </bb-row>
      <div class="d-flex justify-content-between nm-x-15 m-t-15 gap-10">
        <div class="bg-white border-radius-sm col-md-18 p-x-md-20 p-x-15 p-y-15">
          <account-filter/>
        </div>

        <div v-if="!isMobile"
             class="bg-white border-radius-sm button-container flex-1">
          <bb-button
            data-testid="add-pending-payment"
            color="black"
            inverted
            size="md"
            :label="translations.addPendingButtonText"
            class="m-20"
            @click="onCreate"
          />
        </div>
      </div>

      <bb-row v-if="showNoPendingBox">
        <box-with-icon
          class="m-t-md-30 m-t-25 flex-1"
          icon="client-empty-cards">
          <strong class="f-color-gray">{{ translations.noPending.title }}</strong>
          <br>
          <p class="m-t-10">{{ translations.noPending.description }}</p>
        </box-with-icon>
      </bb-row>

      <template v-else>
        <template v-if="isMobile">
          <bb-row v-if="waitingForConfirmation.length > 0">
            <pending-payment-table-mobile
              @onCancel="onCancel"
              @onModify="onModify"
              :pending-payments="waitingForConfirmation"/>
          </bb-row>
          <bb-row v-if="confirmed.length > 0">
            <pending-payment-table-mobile
              :pending-payments="confirmed"
              @onCancel="onCancel"
              @onModify="onModify"
            />
          </bb-row>
          <bb-row v-if="otherStatuses.length > 0">
            <pending-payment-table-mobile
              :pending-payments="otherStatuses"
              @onCancel="onCancel"
            />
          </bb-row>
        </template>
        <template v-else>
          <bb-row v-if="waitingForConfirmation.length > 0" class="m-t-md-30 m-t-25">
            <pending-payment-table-desktop
              @onCancel="onCancel"
              @onModify="onModify"
              :pending-payments="waitingForConfirmation"/>
          </bb-row>

          <bb-row v-if="confirmed.length > 0" class="m-t-md-30 m-t-25">
            <pending-payment-table-desktop
              @onCancel="onCancel"
              @onModify="onModify"
              :pending-payments="confirmed"/>
          </bb-row>

          <bb-row v-if="otherStatuses.length > 0" class="m-t-md-30 m-t-25">
            <pending-payment-table-desktop
              :pending-payments="otherStatuses"
              @onCancel="onCancel"
            />
          </bb-row>
        </template>
      </template>
    </bb-container>
    <modify-pending-modal
      v-if="modifyModalVisible"
      @close="closeModifyModal"
      :modify-payment-id="modifyPendingId"
      :visible="modifyModalVisible"/>
    <cancel-pending-payment-modal
      v-if="cancelModalVisible"
      @close="closeCancelModal"
      :pending-payment-ids="cancelPendingIds"
      :visible="cancelModalVisible"/>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import FullSpinner from '@/layouts/FullSpinner.vue'
import { usePendingPaymentStore } from '@account/store/pendingPaymentStore'
import { AccountRouteName } from '@account/const'
import AccountFilter from '@account/views/payments/pending/AccountFilter.vue'
import { useRootStore } from '@/store/root'
import PendingPaymentTableDesktop from '@account/views/payments/pending/PendingPaymentTableDesktop.vue'
import { OrderStatus } from '@bigbank/dc-common/clients/http/account/modules/pending-payments/pending-payment.types'
import PendingPaymentTableMobile from '@account/views/payments/pending/PendingPaymentTableMobile.vue'
import BoxWithIcon from '@/components/BoxWithIcon.vue'
import CancelPendingPaymentModal from '@account/views/payments/pending/CancelPendingPaymentModal.vue'
import ModifyPendingModal from '@account/views/payments/pending/ModifyPendingModal.vue'

export default {
  components: {
    ModifyPendingModal,
    CancelPendingPaymentModal,
    BoxWithIcon,
    PendingPaymentTableMobile,
    PendingPaymentTableDesktop,
    AccountFilter,
    FullSpinner
  },
  data () {
    return {
      cancelModalVisible: false,
      cancelPendingIds: null,
      modifyModalVisible: false,
      modifyPendingId: null
    }
  },
  computed: {
    ...mapState(usePendingPaymentStore, ['isPendingPaymentsLoading', 'pendingPayments', 'waitingForConfirmation']),
    ...mapState(useRootStore, ['isMobile']),
    translations () {
      return {
        title: {
          account: this.$pgettext('pending_payment', 'Account')
        },
        noPending: {
          title: this.$pgettext('pending_payment', 'You don’t have any pending payments for this account yet '),
          description: this.$pgettext('pending_payment', 'The pending payments section displays transactions that are either in process, or awaiting your approval')
        },
        addPendingButtonText: this.$pgettext('pending_payment', 'Add new payment')
      }
    },
    showNoPendingBox () {
      return this.pendingPayments?.length === 0
    },
    otherStatuses () {
      return this.pendingPayments?.filter(payment => payment.orderStatus !== OrderStatus.NotConfirmed && payment.orderStatus !== OrderStatus.Confirmed)
    },
    confirmed () {
      return this.pendingPayments?.filter(payment => payment.orderStatus === OrderStatus.Confirmed)
    }
  },
  methods: {
    ...mapActions(usePendingPaymentStore, ['searchPendingPayments']),
    onCreate () {
      this.$router.push({ name: AccountRouteName.OutgoingPayments })
    },
    onCancel (pendingPaymentIds) {
      this.cancelModalVisible = true
      this.cancelPendingIds = pendingPaymentIds
    },
    closeCancelModal () {
      this.cancelModalVisible = false
      this.cancelPendingIds = null
    },
    onModify (pendingPaymentId) {
      this.modifyModalVisible = true
      this.modifyPendingId = pendingPaymentId
    },
    closeModifyModal () {
      this.modifyModalVisible = false
      this.modifyPendingId = null
    }
  },
  async created () {
    if (this.pendingPayments.length === 0) {
      await this.searchPendingPayments()
    }
  }
}
</script>

<style lang="scss" scoped>
.pending {
  &__main {
    margin-top: 40px;
    padding-left: 15px;
    padding-right: 15px;

    @media (max-width: $mobile-view-breaking-point) {
      margin-top: 15px;
    }
  }
}

.button-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
